<template>
  <div class="form-input-validated">
    <label :for="inputName">{{inputLabel}}</label>
    <div class="input-wrapper">
      <input
        :disabled="disabled"
        :class="inputComputedClassName"
        :type="inputType"
        :name="inputName"
        :id="inputName"
        :value="inputValue"
        :maxlength="maxlength"
        @input="validateAndSetInput"
        @blur="onBlur"
        @focus="onFocus"
      >
      <span v-if="showSuccessIcon" class="validation-icon success-icon">✓</span>
      <span v-if="showErrorIcon" class="validation-icon error-icon">✕</span>
    </div>
    <span v-if="showError" class="error-message">{{errorText}}</span>
    <span v-if="showSuccess" class="success-message">✓ Câmp valid</span>
  </div>
</template>

<script>
import { notNull, validateEmail, validateCui, validateCNP } from "../../helpers/Validations";

export default {
  props: {
    inputType: {
      type: String,
      default: "text"
    },
    inputName: String,
    inputLabel: String,
    inputValue: String,
    setInput: Function,
    isValid: Boolean,
    disabled: Boolean,
    errorText: String,
    maxlength: {
      type: [String, Number],
      default: null
    },
    validateOnBlur: {
      type: Boolean,
      default: false // Changed to false for real-time validation
    },
    validationType: {
      type: String,
      default: "required" // "required", "email", "cnp"
    },
    forceShowErrors: {
      type: Boolean,
      default: false // Force show errors (e.g., on form submit)
    }
  },
  data() {
    return {
      hasInteracted: false,
      isFocused: false,
      localIsValid: null
    };
  },
  computed: {
    inputComputedClassName: function() {
      let classes = [];
      
      if (this.showError) {
        classes.push("error");
      } else if (this.showSuccess && this.hasInteracted) {
        classes.push("success");
      }
      
      if (this.isFocused) {
        classes.push("focused");
      }
      
      return classes.join(" ");
    },
    showError() {
      // Show error if forced (on submit) or if user has interacted
      if (this.forceShowErrors || this.hasInteracted) {
        return !this.isValid && this.errorText;
      }
      return false;
    },
    showSuccess() {
      // Only show success if user has interacted (not on forced errors)
      if (!this.hasInteracted) return false;
      return this.isValid && this.inputValue && this.inputValue.length > 0;
    },
    showErrorIcon() {
      return this.showError && (this.forceShowErrors || this.hasInteracted);
    },
    showSuccessIcon() {
      return this.showSuccess && this.hasInteracted && !this.isFocused;
    }
  },
  methods: {
    validateAndSetInput(event) {
      const value = event.target.value;
      
      // Mark as interacted as soon as user starts typing (even if empty, to show errors immediately)
      if (!this.hasInteracted) {
        this.hasInteracted = true;
      }
      
      // Update parent state first
      this.setInput(event);
      
      // Real-time validation - always validate as you type
      // The parent's validateField will update Vuex state
      this.validateValue(value);
    },
    onBlur(event) {
      this.isFocused = false;
      this.hasInteracted = true;
      
      if (this.validateOnBlur) {
        this.validateValue(event.target.value);
      }
    },
    onFocus() {
      this.isFocused = true;
    },
    validateValue(value) {
      let isValid = true;
      
      if (this.validationType === "required") {
        isValid = notNull(value);
      } else if (this.validationType === "email") {
        isValid = validateEmail(value);
      } else if (this.validationType === "cnp") {
        isValid = validateCui(value) || validateCNP(value);
      }
      
      this.localIsValid = isValid;
    }
  }
};
</script>

<style lang="scss" scoped>
.form-input-validated {
  position: relative;
}

label {
  display: block;
  margin-bottom: 6px;
  font-size: 14px;
  font-weight: 500;
  color: #64748b;
  line-height: 1.4;
}

.input-wrapper {
  position: relative;
  width: 100%;
}

input {
  display: block;
  padding: 14px 40px 14px 16px; /* Extra padding for icon */
  border: 2px solid #e2e8f0;
  border-radius: 6px;
  font-size: 16px;
  width: 100%;
  transition: all 0.3s ease;
  background-color: #f8fafc;
  font-weight: 500;
  color: #1a1a1a;
  
  &::placeholder {
    color: #94a3b8;
  }
  
  &:hover {
    border-color: #cbd5e1;
    background-color: #f0f4f8;
  }
  
  &:focus {
    outline: none;
    border-color: #bd1f23;
    background-color: #fff;
    box-shadow: 0 0 0 3px rgba(189, 31, 35, 0.1);
  }
  
  &:disabled {
    background-color: #edf2f7;
    cursor: not-allowed;
    color: #a0aec0;
  }
  
  &.error {
    border-color: #e53e3e;
    background-color: #fff;
    animation: shake 0.3s ease;
    
    &:focus {
      border-color: #e53e3e;
      box-shadow: 0 0 0 3px rgba(229, 62, 62, 0.1);
    }
  }
  
  &.success {
    border-color: #10b981;
    background-color: #fff;
    
    &:focus {
      border-color: #10b981;
      box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
    }
  }
  
  &.focused {
    border-color: #bd1f23;
  }
}

.validation-icon {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  pointer-events: none;
  animation: fadeIn 0.3s ease;
}

.success-icon {
  background-color: #10b981;
  color: #fff;
}

.error-icon {
  background-color: #e53e3e;
  color: #fff;
}

.error-message {
  display: block;
  margin: 6px 0 0 0;
  color: #e53e3e;
  font-size: 14px;
  animation: slideDown 0.3s ease;
}

.success-message {
  display: block;
  margin: 6px 0 0 0;
  color: #10b981;
  font-size: 14px;
  font-weight: 500;
  animation: slideDown 0.3s ease;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-5px); }
  75% { transform: translateX(5px); }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-50%) scale(0.8);
  }
  to {
    opacity: 1;
    transform: translateY(-50%) scale(1);
  }
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>

